import CleaningZone, {
  mapCleaningZoneApiToCleaningZone,
} from '@common/interfaces/robot/rooms/cleaning-zone';
import RedZone, { mapRedZoneApiToRedZone } from '@common/interfaces/robot/rooms/red-zone';
import RuleZone, { mapRuleZoneApiToRuleZone } from '@common/interfaces/robot/rooms/rule-zone';
import { LocalRobotState } from './local-robot-slice-definitions';
import Point from '@common/interfaces/robot/rooms/point';
import Gate, { mapGateApiToGate } from '@common/interfaces/robot/rooms/gate';
import IBaseZone from '@common/interfaces/robot/rooms/base-zone';
import RoomData, {
  mapRoomApiDataToRoomData,
  RoomApiData,
} from '@common/interfaces/robot/rooms/room-data';
import { getAllowedZoneShift } from '@common/utils/map/zone-helper';
import {
  mapNodeApiToNode,
  WaypointEdge,
  WaypointNode,
} from '@common/interfaces/robot/rooms/waypoints';
import { updateDataList } from 'utils/generic-data-list-handlers';
import { mapLimitZoneApiToLimitZone } from '@common/interfaces/robot/rooms/room-limit-zone';
import { mapLandmarkApiToLandmark } from '@common/interfaces/robot/rooms/landmark';
import ScheduleEntry, {
  mapScheduleEntryApiToScheduleEntry,
  mapScheduleEntryToScheduleEntryState,
} from '@common/interfaces/robot/scheduling/schedule-entry';
import Mission, { mapMissionApiDataToMission } from '@common/interfaces/robot/missions/mission';
import TeachAndRepeatZonePath, {
  mapTeachAndRepeatZonePathApiToTeachAndRepeatZonePath,
} from '@common/interfaces/robot/rooms/teach-and-repeat-zone-path';

export const selectZoneById = (
  state: LocalRobotState,
  id: string,
): CleaningZone | RedZone | RuleZone | undefined => {
  let foundZone: CleaningZone | RedZone | RuleZone | undefined;

  foundZone = state.cleaningZones.data?.find((zone) => zone.id === id);
  foundZone = foundZone ?? state.redZones.data?.find((zone) => zone.id === id);
  foundZone = foundZone ?? state.ruleZones.data?.find((zone) => zone.id === id);

  return foundZone;
};

export const selectRepeatPathByZoneId = (
  state: LocalRobotState,
  zoneId: string,
): TeachAndRepeatZonePath | undefined => {
  return state.teachRepeatPaths.data?.find((path) => path.zoneId === zoneId);
};

export const selectSelectedRoom = (state: LocalRobotState): RoomData | undefined => {
  return state.rooms.data?.find((r) => r.id === state.rooms.selectedId);
};

export const selectGateByPointId = (
  state: LocalRobotState,
  pointId: string | null,
): Gate | undefined => {
  return state.gates.data?.find((gate) => gate.aPost.id === pointId || gate.bPost.id === pointId);
};

export const selectWaypointNodeById = (
  state: LocalRobotState,
  id: string | null,
): WaypointNode | undefined => {
  if (id == null) return;

  return state.waypoints.data?.nodes.find((node) => node.id === id);
};

export const isPointInsidePolygon = (polygonPoints: Point[], point: Point): boolean => {
  let inside = false;
  for (let i = 0, j = polygonPoints.length - 1; i < polygonPoints.length; j = i++) {
    const xi = polygonPoints[i].coordinateX;
    const yi = polygonPoints[i].coordinateY;
    const xj = polygonPoints[j].coordinateX;
    const yj = polygonPoints[j].coordinateY;

    // prettier-ignore
    const intersect = (yi > point.coordinateY) !== (yj > point.coordinateY) &&
        point.coordinateX < ((xj - xi) * (point.coordinateY - yi)) / (yj - yi) + xi;
    inside = intersect ? !inside : inside;
  }

  return inside;
};

export const shiftZonePoints = (
  zone: RedZone | CleaningZone | RuleZone | IBaseZone,
  initialDragPoint: Point,
  newDragPoint: Point,
  roomData: RoomData,
) => {
  const allowedShift = getAllowedZoneShift(
    {
      coordinateX: newDragPoint.coordinateX - initialDragPoint.coordinateX,
      coordinateY: newDragPoint.coordinateY - initialDragPoint.coordinateY,
    },
    zone.points.map((p) => p.point),
    roomData,
  );

  zone.points.forEach((point) => {
    point.point.coordinateX = allowedShift.coordinateX + point.point.coordinateX;
    point.point.coordinateY = allowedShift.coordinateY + point.point.coordinateY;
  });

  return allowedShift;
};

export const updateStateWithRoomApiData = (state: LocalRobotState, apiData: RoomApiData) => {
  const room = mapRoomApiDataToRoomData(apiData);
  state.rooms.data = updateDataList(room, state.rooms.data);

  state.missions.data = [
    ...state.missions.data.filter((m) => m.room !== room.id),
    ...apiData.missions.map((m) => mapMissionApiDataToMission(m)),
  ];

  const allNonRoomMissionIds = state.missions.data
    .filter((m) => m.room !== apiData.id)
    .flatMap((m) => m.id);
  state.schedules.data = [
    ...state.schedules.data.filter((s) => allNonRoomMissionIds.includes(s.scheduleEntry.missionId)),
    ...apiData.missions
      .flatMap((m) => m.schedules)
      .map(mapScheduleEntryApiToScheduleEntry)
      .map(mapScheduleEntryToScheduleEntryState),
  ];

  state.cleaningZones.data = [
    ...state.cleaningZones.data.filter((cz) => cz.room !== room.id),
    ...apiData.cleaningZones.map((cz) => mapCleaningZoneApiToCleaningZone(cz)),
  ];

  state.teachRepeatPaths.data = [
    ...state.teachRepeatPaths.data.filter((trp) => trp.roomId !== room.id),
    ...apiData.teachAndRepeat.map((trp) =>
      mapTeachAndRepeatZonePathApiToTeachAndRepeatZonePath(trp),
    ),
  ];

  state.redZones.data = [
    ...state.redZones.data.filter((rz) => rz.room !== room.id),
    ...apiData.redZones.map((rz) => mapRedZoneApiToRedZone(rz)),
  ];

  state.limitZones.data = [...state.limitZones.data.filter((lz) => lz.room !== room.id)];
  if (apiData.roomLimitZones.length > 0) {
    state.limitZones.data.push(mapLimitZoneApiToLimitZone(apiData.roomLimitZones[0]));
  }

  state.ruleZones.data = [
    ...state.ruleZones.data.filter((rz) => rz.room !== room.id),
    ...apiData.ruleZones.map((rz) => mapRuleZoneApiToRuleZone(rz)),
  ];

  state.landmarks.data = [
    ...state.landmarks.data.filter((l) => l.room !== room.id),
    ...apiData.landmarks.map((lm) => mapLandmarkApiToLandmark(lm)),
  ];
  state.waypoints.data = {
    nodes: [
      ...state.waypoints.data.nodes.filter((n) => n.room !== room.id),
      ...apiData.waypointNodes.map((n) => mapNodeApiToNode(n)),
    ],
    edges: [
      ...state.waypoints.data.edges.filter((e) => e.room !== room.id),
      ...apiData.waypointEdges,
    ],
  };
  state.gates.data = [
    ...state.gates.data.filter((g) => g.room !== room.id),
    ...mapGateApiToGate(apiData.gates),
  ];
};

export const getNewName = (namePrefix: string, existingNames: string[]) => {
  let newIdNumber = 1;
  while (existingNames.includes(`${namePrefix} ${newIdNumber}`)) {
    newIdNumber++;
  }

  return `${namePrefix} ${newIdNumber}`;
};

export const updateStateWithRoomWaypointSetData = (
  state: LocalRobotState,
  waypointSet: { nodes: WaypointNode[]; edges: WaypointEdge[] },
) => {
  const roomId = state.rooms.selectedId;
  state.waypoints.data.edges = [
    ...state.waypoints.data.edges.filter((edge) => edge.room !== roomId),
    ...waypointSet.edges,
  ];
  state.waypoints.data.nodes = [
    ...state.waypoints.data.nodes.filter((node) => node.room !== roomId),
    ...waypointSet.nodes,
  ];
};

export const filterSchedulesByFloor = (
  filterEntry: ScheduleEntry,
  sourceEntry: ScheduleEntry,
  missions: Mission[],
  rooms: RoomData[],
) => {
  const sourceFloorId = rooms.find(
    (r) => r.id === missions.find((m) => m.id === sourceEntry.missionId)?.room,
  )?.floorId;

  return (
    sourceFloorId != null &&
    rooms.find((r) => r.id === missions.find((m) => m.id === filterEntry.missionId)?.room)
      ?.floorId === sourceFloorId
  );
};
